import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { FileText, Eye, BookOpen, UserCog, Layers, Upload, Shield, PenTool, MapPin, Image, Notebook, Mic, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContentManagementPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [forumStats, setForumStats] = useState({ open: 0, pending: 0 });
  const [loading, setLoading] = useState(true);

  const isFullAdmin = user?.role === 'admin';
  const isSubAdmin = user?.role === 'sub_admin';

  // Iter 104 (RBAC 3-way split): content caps are now orthogonal.
  // Each sub-admin card checks its specific leaf cap. Full admins hold
  // the wildcard, so they see everything.
  const perms = Array.isArray(user?.permissions) ? user.permissions : [];
  const hasCap = (cap) => isFullAdmin || perms.includes('*') || perms.includes(cap);
  const canManageContent = hasCap('manage_content');
  const canReviewSubmissions = hasCap('review_user_submissions');
  const canBulkImport = hasCap('manage_bulk_import');

  useEffect(() => {
    fetchStats();
    fetchForumStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchForumStats = async () => {
    try {
      const response = await axios.get(`${API}/forum/admin/stats`);
      setForumStats({
        open: response.data.open_questions || 0,
        pending: response.data.pending_verification || 0
      });
    } catch (error) {
      console.error('Failed to fetch forum stats:', error);
    }
  };

  // Hardcoded access rules for sub-admins:
  // CAN: Review Submissions, All Questions (no delete/export)
  // CANNOT: Function Management, Topic Management, Surveyor Management, Bulk Import

  const contentModules = [
    {
      title: 'Review Submissions',
      description: 'Review and approve pending question submissions',
      icon: FileText,
      path: '/admin/review',
      color: 'text-[#FF6B6B]',
      bgColor: 'bg-[#FF6B6B]/10',
      stat: stats?.pending_questions,
      statLabel: 'Pending',
      available: canReviewSubmissions
    },
    {
      title: 'Oral Management',
      description: isSubAdmin 
        ? 'View and edit oral exam questions'
        : 'Browse, edit, and manage oral exam questions',
      icon: Mic,
      path: '/admin/questions',
      color: 'text-[#10B981]',
      bgColor: 'bg-[#10B981]/10',
      stat: stats?.approved_questions,
      statLabel: 'Approved',
      available: canManageContent
    },
    {
      title: 'Written Management',
      description: 'Manage written exam papers and questions',
      icon: PenTool,
      path: '/admin/written',
      color: 'text-[#EC4899]',
      bgColor: 'bg-[#EC4899]/10',
      stat: stats?.written_unanswered,
      statLabel: 'Unanswered',
      available: canManageContent
    },
    // Modules 4, 5, 6 - Accessible by sub-admins
    {
      title: 'Picture Manager',
      description: 'Organize and manage your image library',
      icon: Image,
      path: '/admin/picture-manager',
      color: 'text-[#14B8A6]',
      bgColor: 'bg-[#14B8A6]/10',
      stat: stats?.total_disk_mb != null ? `${stats.total_disk_mb} MB` : null,
      statLabel: 'Disk Used',
      available: canManageContent,
      adminOnly: false
    },
    {
      title: 'Engineering Notes',
      description: 'Study material for MEO & ETO grades',
      icon: Notebook,
      path: '/admin/notes/engineering',
      color: 'text-[#6366F1]',
      bgColor: 'bg-[#6366F1]/10',
      stat: stats?.engineering_flagged,
      statLabel: 'Flagged',
      available: canManageContent,
      adminOnly: false,
      showBadge: stats?.engineering_flagged > 0
    },
    {
      title: 'Deck Notes',
      description: 'Study material for Master, Chief Mate & 2MFG',
      icon: Notebook,
      path: '/admin/notes/deck',
      color: 'text-[#EC4899]',
      bgColor: 'bg-[#EC4899]/10',
      stat: stats?.deck_flagged,
      statLabel: 'Flagged',
      available: canManageContent,
      adminOnly: false,
      showBadge: stats?.deck_flagged > 0
    },
    {
      title: 'Forum Management',
      description: 'Moderate community forum questions and answers',
      icon: MessageCircle,
      path: '/admin/forum',
      color: 'text-[#0EA5E9]',
      bgColor: 'bg-[#0EA5E9]/10',
      stat: null,
      statLabel: null,
      forumBadges: true, // Flag to show forum-specific badges
      available: canManageContent,
      adminOnly: false
    },
    // Admin-only modules below
    {
      title: 'Function Management',
      description: 'Manage question functions by grade and number',
      icon: BookOpen,
      path: '/admin/functions',
      color: 'text-[#14B8A6]',
      bgColor: 'bg-[#14B8A6]/10',
      stat: stats?.total_functions,
      statLabel: 'Functions',
      available: isFullAdmin, // Admin only
      adminOnly: true
    },
    {
      title: 'Topic Management',
      description: 'Organize questions by topics and categories',
      icon: Layers,
      path: '/admin/topics',
      color: 'text-[#6366F1]',
      bgColor: 'bg-[#6366F1]/10',
      stat: stats?.total_topics,
      statLabel: 'Topics',
      available: isFullAdmin, // Admin only
      adminOnly: true
    },
    {
      title: 'Surveyor Management',
      description: 'Manage surveyors by department, grades and MMDs',
      icon: UserCog,
      path: '/admin/surveyors',
      color: 'text-[#F59E0B]',
      bgColor: 'bg-[#F59E0B]/10',
      stat: stats?.total_surveyors,
      statLabel: 'Surveyors',
      available: isFullAdmin, // Admin only
      adminOnly: true
    },
    {
      title: 'Bulk Import Questions',
      description: 'Import multiple questions at once using CSV/Excel files',
      icon: Upload,
      path: '/admin/bulk-import',
      color: 'text-[#8B5CF6]',
      bgColor: 'bg-[#8B5CF6]/10',
      stat: stats?.staging_questions_count,
      statLabel: 'In Staging',
      available: canBulkImport,
      adminOnly: !canBulkImport
    },
    {
      title: 'MMD Management',
      description: 'Manage Mercantile Marine Department locations',
      icon: MapPin,
      path: '/admin/mmd',
      color: 'text-[#F97316]',
      bgColor: 'bg-[#F97316]/10',
      stat: stats?.active_mmds != null ? `${stats.active_mmds}/${stats.total_mmds}` : null,
      statLabel: 'Active/Total',
      available: isFullAdmin, // Admin only
      adminOnly: true
    }
  ];

  return (
    <Layout>
      <div className="page-transition">
        {/* Header */}
        <Card className="mb-8 border-l-4 border-l-[#6366F1] shadow-lg">
          <CardHeader className="bg-gradient-to-r from-[#6366F1]/5 to-[#14B8A6]/5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-[#6366F1] rounded-lg">
                <Layers className="w-8 h-8 text-white" />
              </div>
              <div>
                <CardTitle className="text-3xl text-[#6366F1]">Content Management</CardTitle>
                <CardDescription className="text-base mt-1">
                  {isSubAdmin 
                    ? 'Review submissions and edit questions'
                    : 'Manage questions, functions, topics, and surveyors'}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Content Modules Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {contentModules.map((module, index) => (
            module.available && (
              <Card
                key={index}
                onClick={() => navigate(module.path)}
                className="cursor-pointer hover-lift border-2 border-gray-200 hover:border-[#6366F1] transition-all"
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className={`p-3 ${module.bgColor} rounded-lg mb-4`}>
                      <module.icon className={`w-8 h-8 ${module.color}`} />
                    </div>
                    {module.adminOnly && (
                      <Shield className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  <CardTitle className="text-xl">{module.title}</CardTitle>
                  <CardDescription className="text-sm mt-2">
                    {module.description}
                  </CardDescription>
                </CardHeader>
                {(module.stat !== null && module.stat !== undefined) && (
                  <CardContent className="pt-0">
                    <div className="flex items-center gap-2">
                      <div className={`text-2xl font-bold ${module.color}`}>
                        {module.stat}
                      </div>
                      <div className="text-sm text-gray-600">{module.statLabel}</div>
                    </div>
                  </CardContent>
                )}
                {module.forumBadges && (
                  <CardContent className="pt-0">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                        {forumStats.open} Open
                      </Badge>
                      <Badge className={`${forumStats.pending > 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'} hover:bg-amber-100`}>
                        {forumStats.pending} Pending
                      </Badge>
                    </div>
                  </CardContent>
                )}
              </Card>
            )
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default ContentManagementPage;
